import styled from 'styled-components';
import { mq } from '../../../../../theme/mediaQueries';

export const StyledWizard = styled.form`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  max-width: var(--content-max, 1280px);
  margin: 0 auto;
`;

export const StyledWizardBody = styled.div`
  flex: 1;
  padding: var(--s-6, 24px) 0 var(--s-8, 48px);
`;

/* ── Stepper header ─────────────────────────────────────────────── */

export const StyledStepper = styled.ul`
  display: flex;
  align-items: center;
  gap: var(--s-2, 8px);
  list-style: none;
  margin: 0;
  padding: var(--s-4, 16px) 0;
  border-bottom: 1px solid var(--line);
`;

export const StyledStepperItem = styled.li`
  display: flex;
  list-style: none;
`;

export const StyledStepperButton = styled.button<{
  active?: boolean;
  done?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: var(--s-2, 8px);
  padding: var(--s-2, 8px) var(--s-3, 12px);
  border: none;
  border-radius: var(--r-full, 9999px);
  cursor: pointer;
  appearance: none;
  color: ${({ active }) => (active ? 'var(--ink)' : 'var(--muted)')};
  background: ${({ active }) =>
    active ? 'var(--brand-green-soft, rgba(118,177,89,0.12))' : 'transparent'};
  transition: background var(--dur-base, 180ms) var(--ease-out),
    color var(--dur-base, 180ms) var(--ease-out);

  &:hover {
    color: var(--ink);
  }
`;

export const StyledStepperIndex = styled.span<{ active?: boolean; done?: boolean }>`
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  flex: none;
  border-radius: var(--r-full, 9999px);
  font-size: var(--t-xs, 12px);
  font-weight: 600;
  color: ${({ active, done }) =>
    active || done ? 'var(--surface)' : 'var(--muted)'};
  background: ${({ active, done }) =>
    active || done ? 'var(--brand-green)' : 'var(--paper-2, #F2F0EA)'};
`;

export const StyledStepperLabel = styled.span`
  font-size: var(--t-sm, 14px);
  font-weight: 600;
  white-space: nowrap;

  ${mq.ltMd} {
    display: none;
  }
`;

export const StyledStepperOptional = styled.span`
  font-size: var(--t-xs, 12px);
  font-weight: 500;
  color: var(--muted);
  white-space: nowrap;

  ${mq.ltMd} {
    display: none;
  }
`;

/* ── Step layout ────────────────────────────────────────────────── */

export const StyledStepGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--s-4, 16px);
  align-items: start;

  ${mq.ltMd} {
    grid-template-columns: 1fr;
  }
`;

export const StyledStepColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--s-4, 16px);
  min-width: 0;
`;

export const StyledStepCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg, 12px);
  box-shadow: var(--sh-1);
  padding: var(--s-5, 20px);
`;

export const StyledStepCardTitle = styled.h3`
  margin: 0 0 var(--s-4, 16px);
  font-size: var(--t-md, 15px);
  font-weight: 700;
  color: var(--ink);
`;

export const StyledStepHint = styled.p`
  margin: 0 0 var(--s-4, 16px);
  font-size: var(--t-sm, 14px);
  color: var(--muted);
`;

export const StyledFieldHint = styled.p`
  margin: 4px 0 0;
  font-size: var(--t-xs, 12px);
  color: var(--muted);
`;

export const StyledEmptyHint = styled.p`
  margin: 0;
  padding: var(--s-5, 20px);
  border: 1px dashed var(--line);
  border-radius: var(--r-md, 8px);
  font-size: var(--t-sm, 14px);
  color: var(--muted);
  text-align: center;
`;

/* ── Fees banner (replaces the old blocking modal) ──────────────── */

export const StyledFeesBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s-3, 12px);
  margin-bottom: var(--s-4, 16px);
  padding: var(--s-3, 12px) var(--s-4, 16px);
  border: 1px solid var(--warning, #fbb03b);
  border-radius: var(--r-md, 8px);
  background: var(--warning-soft, rgba(251, 176, 59, 0.12));
  font-size: var(--t-sm, 14px);
  color: var(--ink);

  ${mq.ltMd} {
    flex-direction: column;
    align-items: stretch;
  }
`;

/* ── Product search ─────────────────────────────────────────────── */

export const StyledSearchWrap = styled.div`
  margin-bottom: var(--s-4, 16px);
`;

export const StyledSearchResults = styled.ul`
  list-style: none;
  margin: var(--s-3, 12px) 0 0;
  padding: 0;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid var(--line);
  border-radius: var(--r-md, 8px);
`;

export const StyledSearchResultRow = styled.li`
  display: flex;
  align-items: center;
  gap: var(--s-3, 12px);
  padding: var(--s-3, 12px) var(--s-4, 16px);

  &:not(:last-child) {
    border-bottom: 1px solid var(--line);
  }
`;

export const StyledSearchResultName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--t-sm, 14px);
  font-weight: 600;
  color: var(--ink);
`;

export const StyledSearchResultPrice = styled.span`
  flex: none;
  color: var(--muted);
`;

export const StyledSearchLoadMore = styled.div`
  display: flex;
  justify-content: center;
  padding: var(--s-3, 12px);
`;

/* ── Review summary ─────────────────────────────────────────────── */

export const StyledReviewSection = styled.div`
  &:not(:last-child) {
    margin-bottom: var(--s-4, 16px);
    padding-bottom: var(--s-4, 16px);
    border-bottom: 1px solid var(--line);
  }
`;

export const StyledReviewSectionTitle = styled.h4`
  margin: 0 0 var(--s-2, 8px);
  font-size: var(--t-xs, 12px);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
`;

export const StyledReviewRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: var(--s-3, 12px);
  padding: 3px 0;
  font-size: var(--t-sm, 14px);
`;

export const StyledReviewLabel = styled.span`
  color: var(--muted);
  flex: none;
`;

export const StyledReviewValue = styled.span`
  color: var(--ink);
  font-weight: 600;
  text-align: right;
  min-width: 0;
  overflow-wrap: anywhere;
`;

export const StyledReviewTotalRow = styled(StyledReviewRow)`
  font-size: var(--t-md, 15px);

  ${StyledReviewValue} {
    font-weight: 700;
  }
`;

/* ── Missing-info hints (non-blocking) ──────────────────────────── */

export const StyledMissingBanner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--s-2, 8px);
  margin-bottom: var(--s-4, 16px);
  padding: var(--s-3, 12px) var(--s-4, 16px);
  border: 1px solid var(--warning, #fbb03b);
  border-radius: var(--r-md, 8px);
  background: var(--warning-soft, rgba(251, 176, 59, 0.12));
  font-size: var(--t-sm, 14px);
  color: var(--ink);
`;

export const StyledMissingChip = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  padding: 2px 10px;
  border-radius: var(--r-full, 9999px);
  background: var(--surface);
  border: 1px solid var(--warning, #fbb03b);
  font-size: var(--t-xs, 12px);
  font-weight: 600;
  color: var(--ink);

  &:hover {
    background: var(--paper, #faf9f5);
  }
`;

/* ── Footer ─────────────────────────────────────────────────────── */

export const StyledFooter = styled.div`
  position: sticky;
  bottom: var(--s-3, 12px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s-4, 16px);
  padding: var(--s-3, 12px) var(--s-4, 16px);
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg, 14px);
  box-shadow: var(--sh-2);

  ${mq.ltMd} {
    flex-wrap: wrap;
  }
`;

export const StyledFooterTotals = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  margin-inline-start: auto;
`;

export const StyledFooterDeliveryLine = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: var(--s-2, 8px);
  font-size: var(--t-xs, 12px);
  color: var(--muted);
`;

export const StyledFooterGrandTotal = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: var(--s-2, 8px);
  font-size: var(--t-sm, 14px);
  font-weight: 700;
  color: var(--ink);
`;

export const StyledFooterNav = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s-3, 12px);
`;
